// @flow
import React, { useContext } from 'react';
import { FieldArray, getIn, useFormikContext } from 'formik';
import { Grid } from 'semantic-ui-react';

import {
  DEFAULT_VALUE_FOR_DB_ROW_ID_STR,
  NATIVE_INFERENCE_POOL_MODEL_BACKEND_NAME,
  NATIVE_INFERENCE_POOL_TOKEN_BASED_MODELS_FORM,
  NATIVE_INFERENCE_POOL_TOKEN_BASED_MODELS_JSON_CONFIG,
} from '../../../constants';
import { getMonthName } from '../../cluster/cluster-input-table/utils';
import { PrimaryButton } from '../../presentational/buttons/PrimaryButton';
import { SelectField } from '../../../formik-utils/FormFields';
import { PaddedAndRaisedSegment } from '../../presentational/PaddedAndRaisedSegment';
import { getDropdownOptions } from '../../stream-governance/utils';
import { NATIVE_INFERENCE_POOL_DETAILS_CONFIG_MAP } from '../../../configuration/native-inference-pool-details';
import { NativeInferencePoolContext } from '../../../contexts/NativeInferencePoolContext';
import { isAnythingEditedOnNativeInferencePoolPageBesidesTheGivenForm } from '../../cluster/cluster-details-inputs/utils';
import { useEstimateDealDurationMonths } from '../../../custom-hooks/useEstimateDealDurationMonths';
import { markOptionsThatAreAlreadySelectedAsNotSelectable } from '../../../common-utils/utils';

import { NativeInferencePoolTokenBasedModelsMonthlyInputContainerWrapper } from './NativeInferencePoolTokenBasedModelsMonthlyInputContainerWrapper';
import {
  getNativeInferencePoolTokenBasedModelsData,
  getUsedNativeInferencePoolTokenBasedModelNames,
} from './utils';

const getModelWithDefaults = (values, modelDetailsMap, numberOfMonths) => {
  const modelName = getIn(values, NATIVE_INFERENCE_POOL_MODEL_BACKEND_NAME);

  const months = {};
  for (let month = 1; month <= numberOfMonths; month++) {
    const monthName = getMonthName(month);
    months[monthName] = {
      name: monthName,
      month,
      is_enabled: true,
      input_tokens: 0,
      output_tokens: 0,
    };
  }

  return {
    modelId: DEFAULT_VALUE_FOR_DB_ROW_ID_STR,
    modelName,
    modelLabel: modelDetailsMap.get(modelName)?.model_label ?? modelName,
    months,
  };
};

export const NativeInferencePoolTokenBasedUsageContainer = () => {
  const { values, initialValues, setFieldValue } = useFormikContext();
  const nativeInferencePool = useContext(NativeInferencePoolContext);
  const numberOfMonths = useEstimateDealDurationMonths();

  const modelOptionsConfig = NATIVE_INFERENCE_POOL_DETAILS_CONFIG_MAP.get(
    NATIVE_INFERENCE_POOL_MODEL_BACKEND_NAME
  );
  const modelDetails = nativeInferencePool?.native_inference_configs?.model_types ?? [];
  const modelDetailsMap = new Map(
    modelDetails.map((model) => [model[NATIVE_INFERENCE_POOL_MODEL_BACKEND_NAME], model])
  );

  const modelsData = getNativeInferencePoolTokenBasedModelsData(values);
  const usedModelNames = getUsedNativeInferencePoolTokenBasedModelNames(modelsData);

  const allModelOptions = getDropdownOptions(
    modelDetails,
    values,
    modelOptionsConfig,
    false,
    false,
    false,
    true
  );

  const modelOptions = markOptionsThatAreAlreadySelectedAsNotSelectable(
    allModelOptions,
    usedModelNames
  );

  const disabled = isAnythingEditedOnNativeInferencePoolPageBesidesTheGivenForm(
    values,
    initialValues,
    NATIVE_INFERENCE_POOL_TOKEN_BASED_MODELS_FORM
  );

  const keyNameInValues = NATIVE_INFERENCE_POOL_TOKEN_BASED_MODELS_JSON_CONFIG;

  return (
    <PaddedAndRaisedSegment disabled={disabled}>
      <FieldArray
        name={`${keyNameInValues}.models`}
        render={(arrayHelpers) => (
          <>
            <Grid>
              <Grid.Row columns={3}>
                <Grid.Column verticalAlign="bottom">
                  <SelectField
                    disabled={disabled}
                    fieldDisplayName={modelOptionsConfig.displayName}
                    fieldName={modelOptionsConfig.backendFieldName}
                    options={modelOptions}
                    search={true}
                  />
                </Grid.Column>
                <Grid.Column verticalAlign="bottom">
                  <PrimaryButton
                    disabled={!getIn(values, NATIVE_INFERENCE_POOL_MODEL_BACKEND_NAME) || disabled}
                    onClick={() => {
                      arrayHelpers.push(
                        getModelWithDefaults(values, modelDetailsMap, numberOfMonths)
                      );
                      setFieldValue(NATIVE_INFERENCE_POOL_MODEL_BACKEND_NAME, null);
                    }}
                    size="small"
                    text="Add Model"
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
            <NativeInferencePoolTokenBasedModelsMonthlyInputContainerWrapper
              arrayHelpers={arrayHelpers}
              disabled={disabled}
            />
          </>
        )}
      />
    </PaddedAndRaisedSegment>
  );
};
